import { ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { useLang } from "@/lib/i18n";
import { useSite } from "@/lib/site";
import { Reveal } from "@/components/Reveal";
import PageHero from "@/components/PageHero";
import Seo from "@/components/Seo";
import Marquee from "@/components/Marquee";
import HEROES from "@/lib/pageHeroes.json";

// Abu Dhabi — towers above the Gulf

export default function About() {
  const { t, L } = useLang();
  const site = useSite();
  const s = site?.settings || {};
  const stats = s.stats || [];

  return (
    <div data-testid="about-page">
      <Seo page="about" />
      <PageHero eyebrow={t("about_eyebrow")} title={t("about_title")} sub={L(s.hero_tagline)} image={HEROES["about"]} />

      <section className="mx-auto max-w-7xl px-5 sm:px-8 py-20 md:py-28">
        <div className="grid gap-6 md:grid-cols-2">
          {[
            { title: t("mission"), body: L(s.mission), testid: "mission-card" },
            { title: t("vision"), body: L(s.vision), testid: "vision-card" },
          ].map((c, i) => (
            <Reveal key={c.title} delay={0.08 * i}>
              <div
                data-testid={c.testid}
                className="h-full border-t-2 border-primary bg-card/40 p-9 md:p-11 card-lift"
                style={{ borderRadius: "var(--radius)" }}
              >
                <p className="eyebrow mb-4">{c.title}</p>
                <p className="font-head text-2xl md:text-3xl leading-snug">{c.body}</p>
              </div>
            </Reveal>
          ))}
        </div>

        <Reveal className="mt-16">
          <div data-testid="about-intro" className="border border-border p-9 md:p-12" style={{ borderRadius: "var(--radius)" }}>
            <p className="eyebrow mb-5">{t("about_eyebrow")}</p>
            <p className="font-head text-2xl md:text-4xl leading-snug max-w-4xl">{L(s.about_intro)}</p>
          </div>
        </Reveal>

        <div className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-8">
          {stats.map((st, i) => (
            <Reveal key={i} delay={0.06 * i} data-testid={`about-stat-${i}`}>
              <p className="font-head text-4xl md:text-5xl text-primary">{st.value}</p>
              <p className="mt-2 text-xs tracking-[0.2em] uppercase text-muted-foreground">{L(st.label)}</p>
            </Reveal>
          ))}
        </div>
      </section>

      <section data-testid="governance-section" className="border-y border-border bg-card/40">
        <div className="mx-auto max-w-7xl px-5 sm:px-8 py-20 md:py-28 grid md:grid-cols-2 gap-12 items-center">
          <Reveal>
            <p className="eyebrow mb-4">{t("governance_eyebrow")}</p>
            <h2 className="font-head text-3xl sm:text-4xl lg:text-5xl leading-[1.1]">{t("governance_title")}</h2>
            <p className="mt-6 text-muted-foreground text-base sm:text-lg leading-relaxed">{t("governance_body")}</p>
            <Link to="/leadership" data-testid="governance-leadership-link" className="btn-outline mt-9">
              {t("nav_leadership")} <ArrowRight className="h-4 w-4 rtl:rotate-180" />
            </Link>
          </Reveal>
          <Reveal delay={0.12}>
            <div className="grid gap-4">
              {(site?.sectors || []).map((sec, i) => (
                <div key={sec.id} className="flex items-center gap-5 border-b border-border pb-4">
                  <span className="font-head text-2xl text-primary">{String(i + 1).padStart(2, "0")}</span>
                  <span className="text-lg">{L(sec.name)}</span>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      <div className="py-20"><Marquee /></div>
    </div>
  );
}
